<template>
  <div class="analysis">
    <SchoolPicker
      :schools="activeGroup?.schools ?? []"
      :selected-id="selectedSchoolId"
      @select="selectSchool"
    />

    <SkillPicker
      :skills="selectedSchool?.skills ?? []"
      :selected-id="selectedSkillId"
      @select="selectSkill"
    />

    <section class="workspace gm-panel gm-panel--flush">
      <template v-if="selectedSkill">
        <header class="workspace__head" :style="{ '--school-accent': selectedSchool?.accent }">
          <GlyphIcon
            :src="selectedSkill.icon"
            :label="selectedSkill.name"
            :accent="selectedSchool?.accent"
            size="3rem"
          />

          <div class="ident">
            <p class="gm-eyebrow ident__school">
              <GlyphIcon
                :src="selectedSchool?.icon"
                :label="selectedSchool?.name ?? ''"
                :accent="selectedSchool?.accent"
                size="1rem"
              />
              {{ selectedSchool?.name }}
            </p>
            <h2 class="ident__name">{{ selectedSkill.name }}</h2>
            <p class="ident__desc">{{ selectedSkill.description }}</p>
          </div>

          <ul class="facts">
            <li v-for="fact in facts" :key="fact.label">
              <span class="facts__value">{{ fact.value }}</span>
              <span class="facts__label">{{ fact.label }}</span>
            </li>
          </ul>
        </header>

        <div class="workspace__toolbar">
          <span class="workspace__axis">
            Plotted against <strong>{{ xAxisLabel }}</strong>
          </span>
          <ChartSettings v-model="chartConfig" @reset="resetConfig" />
        </div>

        <SkillImpactChart
          :impact-data="chartData"
          :x-axis-label="xAxisLabel"
          :loading="loading"
        />

        <ImpactStrip v-if="selectedSkill.impact.length" :impacts="selectedSkill.impact" />
      </template>

      <div v-else class="gm-empty workspace__empty">
        <div class="gm-empty__title">{{ emptyTitle }}</div>
        <p class="gm-empty__hint">{{ emptyHint }}</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import ChartSettings from './ChartSettings.vue';
import GlyphIcon from './GlyphIcon.vue';
import ImpactStrip from './ImpactStrip.vue';
import SchoolPicker from './SchoolPicker.vue';
import SkillImpactChart from './SkillImpactChart.vue';
import SkillPicker from './SkillPicker.vue';
import { gradeLabel } from '@/constants/grades.js';
import { cellsIn } from '@/utils/spray.js';
import { generateSkillChartData, getPrimaryScalingStat } from '@/composables/useSkillCalculator';

const props = defineProps({
  groups: { type: Array, required: true },
  activeGroupId: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const DEFAULT_CONFIG = {
  minStat: 10,
  maxStat: 100,
  statStep: 5,
  diceMin: 0.5,
  diceMax: 1.5,
};

const selectedSchoolId = ref(null);
const selectedSkillId = ref(null);
const chartConfig = ref({ ...DEFAULT_CONFIG });

const activeGroup = computed(
  () => props.groups.find((group) => group.id === props.activeGroupId) ?? null,
);

const selectedSchool = computed(
  () => activeGroup.value?.schools.find((school) => school.id === selectedSchoolId.value) ?? null,
);

const selectedSkill = computed(
  () => selectedSchool.value?.skills.find((skill) => skill.id === selectedSkillId.value) ?? null,
);

// The X axis is whichever stat the first impact leans on hardest — plotting
// against a stat the formula ignores would draw a flat line and read as a bug
// in the skill rather than in the axis.
const xAxisLabel = computed(() => {
  const impact = selectedSkill.value?.impact[0];
  return (impact && getPrimaryScalingStat(impact.formula)) || 'Stat Value';
});

const chartData = computed(() =>
  selectedSkill.value ? generateSkillChartData(selectedSkill.value, chartConfig.value) : [],
);

const facts = computed(() => {
  const skill = selectedSkill.value;
  if (!skill) return [];
  const range =
    skill.min_range === skill.max_range
      ? String(skill.max_range ?? '—')
      : `${skill.min_range ?? 0}–${skill.max_range ?? 0}`;
  return [
    { label: 'Grade', value: gradeLabel(skill.grade) },
    { label: 'Type', value: skill.type ?? '—' },
    { label: 'Range', value: range },
    { label: 'Cells covered', value: cellsIn(skill.spray_config) },
    ...skill.cost.map((cost) => ({ label: cost.kind, value: cost.value })),
  ];
});

const emptyTitle = computed(() => (selectedSchool.value ? 'No skill selected' : 'No school selected'));

const emptyHint = computed(() =>
  selectedSchool.value
    ? 'Pick a skill from the list. Skills are ordered by grade, weakest tier first, so the list reads as the order a character learns them.'
    : 'Pick a school to see the skills it teaches, then a skill to plot its impact across the stat range.',
);

function selectSchool(id) {
  selectedSchoolId.value = id;
  selectedSkillId.value = null;
}

function selectSkill(id) {
  selectedSkillId.value = id;
}

function resetConfig() {
  chartConfig.value = { ...DEFAULT_CONFIG };
}

// A school from the previous path would keep its skills on screen under the new
// path's heading, so the selection follows the path rather than outliving it.
watch(
  () => props.activeGroupId,
  () => {
    selectSchool(activeGroup.value?.schools[0]?.id ?? null);
  },
  { immediate: true },
);
</script>

<style scoped>
.analysis {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  gap: var(--gm-gap-sm);
}

.workspace {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.workspace__head {
  display: flex;
  align-items: flex-start;
  gap: var(--gm-gap);
  padding: var(--gm-gap);
  border-bottom: 1px solid var(--gm-border-dim);
}

.ident {
  flex: 1;
  min-width: 0;
}

.ident__school {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  color: var(--school-accent, var(--gm-text-muted));
}

.ident__name {
  margin: 0.1rem 0 0;
  font-family: var(--gm-font-display);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gm-text);
}

.ident__desc {
  margin: 0.25rem 0 0;
  font-size: 0.76rem;
  line-height: 1.4;
  color: var(--gm-text-muted);
}

.facts {
  flex: 0 0 auto;
  display: flex;
  gap: var(--gm-gap);
  margin: 0;
  padding: 0;
  list-style: none;
  flex-wrap: wrap;
  justify-content: flex-end;
  max-width: 26rem;
}

.facts li {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
}

.facts__value {
  font-family: var(--gm-font-display);
  font-size: 0.78rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--gm-accent);
  white-space: nowrap;
}

.facts__label {
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

.workspace__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gm-gap);
  flex-wrap: wrap;
  padding: var(--gm-gap-sm) var(--gm-gap);
  background: var(--gm-surface-sunken);
  border-bottom: 1px solid var(--gm-border-dim);
}

.workspace__axis {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
}

.workspace__axis strong {
  color: var(--gm-accent);
}

.workspace__empty {
  flex: 1;
  justify-content: center;
  max-width: 32rem;
}

@media (max-width: 1100px) {
  .facts {
    max-width: 14rem;
  }
}
</style>
